/**
 * The sizes a bush or shrub line is offered in — the office's side (server: services/bushSizes).
 *
 * A planting line is one job at several prices: a 1-gallon boxwood and a 15-gallon one are the same hole and a very
 * different plant. Tick the sizes to offer, type a price for each, and pick the one the bid shows until the customer
 * chooses. The sizes come from the picker's catalog (1, 3, 5, 7, 15 gallon until he adds to it) and any size typed
 * here is added to it, so the next bid offers it too.
 *
 * Every price is typed by the office. Nothing here computes a price from a size.
 */
import React, { useState } from 'react';
import { Check, Plus, Trash2 } from 'lucide-react';

export type SizeCatalogItem = { key: string; label: string; gallons?: number };
export type SizeOption = { key: string; label: string; price: number };

const money = (n: number) => { const v = Math.round(Number(n) * 100) / 100; return `$${v.toLocaleString('en-US', { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 })}`; };
// The server's own key rule (services/bushSizes.keyFor): a size in gallons is always gal_<n> however it is typed, so
// "15 gal" typed here is the catalog's "15 gallon" and not a second copy of it.
const slug = (label: string) => {
  const m = /(\d+(?:\.\d+)?)\s*(?:gal|gallon)/i.exec(label);
  if (m && Number(m[1]) > 0) return `gal_${String(Number(m[1])).replace('.', '_')}`;
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 32);
};

export default function BushSizeEditor({ catalog, options, chosen, quantity = 1, busy = false, onSave, onCancel }: {
  catalog: SizeCatalogItem[];
  /** What the line offers today ([] = nothing yet). */
  options: SizeOption[];
  /** The size the bid is on now. */
  chosen?: string | null;
  quantity?: number;
  busy?: boolean;
  /** `sizes` [] takes the sizes off the line; `keep` is the size the bid shows until they pick. */
  onSave: (sizes: SizeOption[], keep: string | null) => void;
  onCancel: () => void;
}) {
  const [rows, setRows] = useState<SizeOption[]>(() => options.map(o => ({ ...o })));
  const [keep, setKeep] = useState<string | null>(chosen || (options[0]?.key ?? null));
  const [newLabel, setNewLabel] = useState('');

  const has = (key: string) => rows.some(r => r.key === key);
  const add = (label: string, price = 0) => {
    const key = slug(label);
    if (!key || has(key)) return;
    setRows(r => [...r, { key, label: label.trim(), price }]);
    setKeep(k => k || key);
  };
  const remove = (key: string) => {
    setRows(r => r.filter(x => x.key !== key));
    setKeep(k => (k === key ? null : k));
  };
  const setPrice = (key: string, price: string) => setRows(r => r.map(x => (x.key === key ? { ...x, price: price === '' ? NaN : Number(price) } : x)));

  // A size with no price typed is not offered — a bid must never show a blank. Said, not silently dropped.
  const priced = rows.filter(r => Number.isFinite(r.price) && r.price >= 0).map(r => ({ ...r, price: Math.round(r.price * 100) / 100 }));
  const missing = rows.length - priced.length;
  const keepKey = priced.some(p => p.key === keep) ? keep : (priced[0]?.key ?? null);

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-3">
      <div>
        <p className="text-xs font-semibold text-gray-900">Sizes to offer on this line</p>
        <p className="text-[11px] text-gray-600">They pick one on the bid and the total updates. The dot marks the size the bid shows until they choose.</p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {catalog.filter(c => !has(c.key)).map(c => (
          <button key={c.key} type="button" onClick={() => add(c.label)}
            className="px-2 py-1 rounded-full border border-emerald-300 bg-white text-[11px] font-medium text-emerald-800 hover:bg-emerald-100">
            <Plus className="inline h-3 w-3 -mt-0.5 mr-0.5" />{c.label}
          </button>
        ))}
      </div>

      {rows.length === 0 && <p className="text-[11px] text-gray-500">No sizes yet — tap one above, or type your own.</p>}

      {rows.length > 0 && (
        <ul className="space-y-1.5">
          {rows.map(r => {
            const ok = Number.isFinite(r.price) && r.price >= 0;
            return (
              <li key={r.key} className="flex items-center gap-2">
                <button type="button" onClick={() => ok && setKeep(r.key)} title="Show this size on the bid until they pick"
                  aria-pressed={keepKey === r.key}
                  className={`h-5 w-5 flex-shrink-0 rounded-full border flex items-center justify-center ${keepKey === r.key ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-gray-300 text-transparent'}`}>
                  <Check className="h-3 w-3" />
                </button>
                <span className="text-xs text-gray-900 flex-1 min-w-0 truncate">{r.label}</span>
                <span className="text-[11px] text-gray-500">$</span>
                <input type="number" step="0.01" min="0" value={Number.isFinite(r.price) ? String(r.price) : ''}
                  onChange={e => setPrice(r.key, e.target.value)}
                  className={`input text-xs w-24 ${ok ? '' : 'border-amber-400'}`} placeholder="each" />
                {quantity !== 1 && ok && <span className="text-[11px] text-gray-500 whitespace-nowrap">= {money(r.price * quantity)}</span>}
                <button type="button" onClick={() => remove(r.key)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex gap-2">
        <input value={newLabel} onChange={e => setNewLabel(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && newLabel.trim()) { add(newLabel); setNewLabel(''); } }}
          className="input text-xs flex-1" placeholder="Another size (e.g. 30 gallon, 2 inch caliper)" maxLength={40} />
        <button type="button" onClick={() => { if (newLabel.trim()) { add(newLabel); setNewLabel(''); } }}
          disabled={!newLabel.trim()} className="btn-secondary text-xs py-1 px-3">Add</button>
      </div>

      {missing > 0 && <p className="text-[11px] text-amber-700">{missing} size{missing === 1 ? '' : 's'} with no price {missing === 1 ? 'is' : 'are'} not offered — type a price or remove it.</p>}

      <div className="flex gap-2">
        <button type="button" onClick={() => onSave(priced, keepKey)} disabled={busy}
          className="btn-primary text-xs py-1 px-3">{busy ? 'Saving…' : priced.length ? `Offer ${priced.length} size${priced.length === 1 ? '' : 's'}` : 'Save'}</button>
        <button type="button" onClick={onCancel} className="btn-secondary text-xs py-1 px-3">Cancel</button>
        {options.length > 0 && (
          <button type="button" onClick={() => onSave([], null)} disabled={busy}
            className="text-xs text-gray-500 underline ml-auto">Take sizes off this line</button>
        )}
      </div>
    </div>
  );
}
